import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { 
  Loader2, 
  Clock, 
  MapPin,
  Phone,
  User,
  Calendar,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Shield,
  HardHat,
  Hammer,
  Ban,
  AlertOctagon,
  Scale,
  Package
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PublicSubcontractorSOW = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [sowData, setSowData] = useState(null);
  const [company, setCompany] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  // Enable scrolling for this page
  useEffect(() => {
    document.body.classList.add('public-proposal-page');
    return () => {
      document.body.classList.remove('public-proposal-page');
    };
  }, []);

  const fetchSOW = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/public/subcontractor-sow/${token}`);
      setSowData(res.data);
      setCompany(res.data.company);
    } catch (error) {
      console.error("Failed to load SOW", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSOW();
  }, [fetchSOW]);

  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#020617" }}>
        <Loader2 className="h-12 w-12 text-amber-400 animate-spin" />
      </div>
    );
  }

  // Not found state
  if (!sowData) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#020617" }}>
        <Toaster position="top-center" />
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-slate-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Scope of Work Not Found</h1>
          <p className="text-slate-400">This link may have expired or is invalid.</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen py-8"
      style={{ 
        backgroundColor: "#020617",
        fontFamily: "'Inter', sans-serif",
        overflowY: "auto",
        overflowX: "hidden",
        height: "auto",
        minHeight: "100vh"
      }}
    >
      <Toaster position="top-center" />
      
      {/* Document Container */}
      <div 
        className="mx-auto px-4"
        style={{ 
          maxWidth: "900px",
          backgroundColor: "#0f172a",
          borderRadius: "8px",
          border: "1px solid rgba(245, 158, 11, 0.3)",
          boxShadow: "0 0 40px rgba(0, 0, 0, 0.5)"
        }}
      >
        {/* Header */}
        <div 
          className="px-10 pt-8 pb-6"
          style={{
            background: "linear-gradient(180deg, rgba(245, 158, 11, 0.15) 0%, transparent 100%)",
            borderBottom: "1px solid rgba(245, 158, 11, 0.3)",
            borderRadius: "8px 8px 0 0"
          }}
        >
          {/* Company Name */}
          <div className="mb-6">
            <h1 
              className="text-2xl font-bold tracking-wide"
              style={{ color: "#f59e0b" }}
            >
              {company?.company_name || sowData?.company_name || "GENERAL CONTRACTOR"}
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              {company?.address || "Licensed General Contractor"}
            </p>
          </div>
          
          {/* Document Title */}
          <div className="flex items-center gap-4 mb-4">
            <HardHat className="h-8 w-8 text-amber-400" />
            <div>
              <h2 className="text-white font-bold text-xl uppercase tracking-wider">SUBCONTRACTOR SCOPE OF WORK</h2>
              <p className="text-amber-400 text-sm font-mono">{sowData?.document_id || "SUB-SOW-00001"}</p>
            </div>
          </div>
          
          {/* Meta Info */}
          <div className="flex items-center gap-8 flex-wrap mt-4">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">DATE ISSUED</p>
              <p className="text-white font-bold text-sm">{formatDate(sowData?.created_at)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">CONTRACT VALUE</p>
              <p className="text-amber-400 font-bold text-sm">{fmt(sowData?.total_value)}</p>
            </div>
          </div>
        </div>

        {/* Subcontractor Info */}
        <div className="px-10 py-6" style={{ borderBottom: "1px solid rgba(100, 116, 139, 0.2)" }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div 
              className="p-4 rounded-lg"
              style={{ backgroundColor: "rgba(245, 158, 11, 0.05)", border: "1px solid rgba(245, 158, 11, 0.2)" }}
            >
              <p className="text-xs text-amber-400 uppercase tracking-widest mb-2">SUBCONTRACTOR</p>
              <p className="text-white font-bold">{sowData?.subcontractor_name || "Subcontractor"}</p>
              <p className="text-slate-400 text-sm">{sowData?.subcontractor_company || ""}</p>
              <p className="text-slate-400 text-sm">{sowData?.subcontractor_phone || ""}</p>
              <p className="text-slate-400 text-sm">{sowData?.subcontractor_email || ""}</p>
              {sowData?.subcontractor_license && (
                <p className="text-slate-500 text-xs mt-2">License: {sowData.subcontractor_license}</p>
              )}
            </div>
            <div 
              className="p-4 rounded-lg"
              style={{ backgroundColor: "rgba(245, 158, 11, 0.05)", border: "1px solid rgba(245, 158, 11, 0.2)" }}
            >
              <p className="text-xs text-amber-400 uppercase tracking-widest mb-2">JOB SITE</p>
              <p className="text-white font-bold">{sowData?.project_name || "Project"}</p>
              <p className="text-slate-400 text-sm">{sowData?.project_address || "TBD"}</p>
              <p className="text-slate-500 text-xs mt-2">Work Hours: {sowData?.work_hours || "7:00 AM - 6:00 PM"}</p>
            </div>
          </div>
        </div>

        {/* Critical Terms - RED WARNING BOX */}
        {sowData?.critical_terms && sowData.critical_terms.length > 0 && (
          <div className="px-10 py-6" style={{ borderBottom: "1px solid rgba(100, 116, 139, 0.2)" }}>
            <div className="flex items-center gap-2 mb-4">
              <AlertOctagon className="h-5 w-5 text-red-400" />
              <p className="text-xs text-red-400 uppercase tracking-wider font-bold">⚠️ CRITICAL TERMS - READ CAREFULLY</p>
            </div>
            <div className="space-y-4">
              {sowData.critical_terms.map((term, idx) => (
                <div 
                  key={idx}
                  className="p-4 rounded-lg"
                  style={{ 
                    backgroundColor: term.type === "danger" ? "rgba(239, 68, 68, 0.1)" : 
                                    term.type === "warning" ? "rgba(245, 158, 11, 0.1)" : "rgba(59, 130, 246, 0.1)",
                    border: term.type === "danger" ? "1px solid rgba(239, 68, 68, 0.3)" : 
                            term.type === "warning" ? "1px solid rgba(245, 158, 11, 0.3)" : "1px solid rgba(59, 130, 246, 0.3)"
                  }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {term.type === "danger" ? <Ban className="h-4 w-4 text-red-400" /> : 
                     term.type === "warning" ? <AlertTriangle className="h-4 w-4 text-amber-400" /> : 
                     <Shield className="h-4 w-4 text-blue-400" />}
                    <p 
                      className="font-bold text-sm uppercase"
                      style={{ 
                        color: term.type === "danger" ? "#f87171" : 
                               term.type === "warning" ? "#fbbf24" : "#60a5fa"
                      }}
                    >
                      {term.title}
                    </p>
                  </div>
                  <p className="text-slate-300 text-sm">{term.content}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Trade Sections */}
        {sowData?.trade_sections && sowData.trade_sections.length > 0 && (
          <div className="px-10 py-6" style={{ borderBottom: "1px solid rgba(100, 116, 139, 0.2)" }}>
            <div className="flex items-center gap-2 mb-4">
              <Hammer className="h-5 w-5 text-amber-400" />
              <p className="text-xs text-amber-400 uppercase tracking-wider font-bold">SCOPE OF WORK - DETAILED INSTRUCTIONS</p>
            </div>
            <div className="space-y-4">
              {sowData.trade_sections.map((section, idx) => {
                const isExpanded = expandedSections[`trade-${idx}`] !== false;
                return (
                  <div 
                    key={idx}
                    className="rounded-lg overflow-hidden"
                    style={{ border: "1px solid rgba(245, 158, 11, 0.3)" }}
                  >
                    <button
                      onClick={() => toggleSection(`trade-${idx}`)}
                      className="w-full px-5 py-4 flex items-center justify-between"
                      style={{ backgroundColor: "rgba(245, 158, 11, 0.1)" }}
                    >
                      <div className="flex items-center gap-3">
                        <HardHat className="h-5 w-5 text-amber-400" />
                        <span className="text-white font-bold">{section.name}</span>
                        {section.total && <span className="text-amber-400 text-sm ml-2">{fmt(section.total)}</span>}
                      </div>
                      {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                    </button>
                    
                    {isExpanded && (
                      <div className="p-5" style={{ backgroundColor: "rgba(15, 23, 42, 0.5)" }}>
                        {/* Work Items */}
                        {section.work_items && section.work_items.length > 0 && (
                          <div className="mb-4">
                            <p className="text-xs text-amber-400 uppercase tracking-wider font-bold mb-2">WORK ITEMS</p>
                            <ul className="space-y-2">
                              {section.work_items.map((item, itemIdx) => (
                                <li key={itemIdx} className="flex items-start gap-3">
                                  <CheckCircle2 className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                                  <span className="text-slate-300">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Materials */}
                        {section.materials && section.materials.length > 0 && (
                          <div className="mb-4">
                            <p className="text-xs text-blue-400 uppercase tracking-wider font-bold mb-2">MATERIALS (GC-PROVIDED)</p>
                            <div className="flex flex-wrap gap-2">
                              {section.materials.map((material, matIdx) => (
                                <span 
                                  key={matIdx}
                                  className="px-3 py-1 rounded text-sm text-blue-300"
                                  style={{ backgroundColor: "rgba(59, 130, 246, 0.15)", border: "1px solid rgba(59, 130, 246, 0.3)" }}
                                >
                                  {material}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Quality Standards */}
                        {section.quality_standards && section.quality_standards.length > 0 && (
                          <div>
                            <p className="text-xs text-emerald-400 uppercase tracking-wider font-bold mb-2">QUALITY STANDARDS</p>
                            <ul className="space-y-1">
                              {section.quality_standards.map((standard, stdIdx) => (
                                <li key={stdIdx} className="flex items-start gap-2 text-slate-400 text-sm">
                                  <span className="text-emerald-400">•</span>
                                  {standard}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Payment Milestones */}
        {sowData?.payment_milestones && sowData.payment_milestones.length > 0 && (
          <div className="px-10 py-6" style={{ borderBottom: "1px solid rgba(100, 116, 139, 0.2)" }}>
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="h-5 w-5 text-amber-400" />
              <p className="text-xs text-amber-400 uppercase tracking-wider font-bold">PAYMENT MILESTONES</p>
            </div>
            <div className="space-y-3">
              {sowData.payment_milestones.map((milestone, idx) => (
                <div 
                  key={idx}
                  className="p-4 rounded-lg"
                  style={{ backgroundColor: "rgba(245, 158, 11, 0.05)", border: "1px solid rgba(245, 158, 11, 0.2)" }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-white font-medium">{milestone.name}</p>
                      <p className="text-slate-500 text-sm">{milestone.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-amber-400 font-bold">{milestone.percentage}%</p>
                      <p className="text-slate-500 text-sm">{fmt(sowData.total_value * milestone.percentage / 100)}</p>
                    </div>
                  </div>
                  {milestone.criteria && (
                    <div className="mt-2 pt-2 border-t border-slate-700/50">
                      <p className="text-xs text-slate-500 uppercase mb-1">Completion Criteria:</p>
                      <p className="text-slate-400 text-sm">{milestone.criteria}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* General Terms */}
        {sowData?.general_terms && sowData.general_terms.length > 0 && (
          <div className="px-10 py-6" style={{ borderBottom: "1px solid rgba(100, 116, 139, 0.2)" }}>
            <div className="flex items-center gap-2 mb-4">
              <Scale className="h-5 w-5 text-amber-400" />
              <p className="text-xs text-amber-400 uppercase tracking-wider font-bold">GENERAL TERMS</p>
            </div>
            <ul className="space-y-2">
              {sowData.general_terms.map((term, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="text-amber-400 mt-1">•</span>
                  <span className="text-slate-400">{term}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer */}
        <div 
          className="px-10 py-6 text-center"
          style={{
            borderTop: "1px solid rgba(100, 116, 139, 0.2)",
            backgroundColor: "rgba(15, 23, 42, 0.5)"
          }}
        >
          <p className="text-slate-500 text-xs">
            {company?.company_name || "General Contractor"} • Subcontractor Scope of Work
          </p>
          <p className="text-slate-600 text-xs mt-1">
            Questions? Contact the General Contractor
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicSubcontractorSOW;
